import React, { useCallback, useState } from 'react';

import {
  ALINE_TOOL_NAMES,
  getExternalEnableDescription,
  getExternalEnableTitle,
  getExternalToolActionLabel,
} from '@aline/common';
import { Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { useChatAdapter } from 'src/components/ai/chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import {
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './PendingInteractionCardChrome';

const useStyles = makeStyles()(theme => ({
  body: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 14,
    whiteSpace: 'pre-wrap',
  },
  slugs: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.secondaryType,
    fontSize: 14,
    marginTop: theme.spacing(1),
  },
}));

export const EnableExternalToolsCard = React.memo(function EnableExternalToolsCard({
  maxHeight,
  messageId,
  queuePosition,
  requests,
  toolkit,
  unionSlugs,
}: {
  messageId: string;
  requests: readonly { toolCallId: string; toolSlugs: readonly string[] }[];
  toolkit: string;
  unionSlugs: readonly string[];
  maxHeight?: number | null;
  queuePosition?: { current: number; total: number };
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { addToolOutputs } = useChatAdapter();
  const [expanded, setExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const answerPause = useCallback(
    async (status: 'enabled' | 'dismissed') => {
      if (submitting) return;
      setSubmitting(true);
      try {
        await addToolOutputs(
          requests.map(request => ({
            messageId,
            output: { status, toolSlugs: request.toolSlugs, toolkit },
            tool: ALINE_TOOL_NAMES.enableExternalTools,
            toolCallId: request.toolCallId,
          }))
        );
      } catch (error) {
        setSubmitting(false);
        Sentry.captureException(error);
        enqueueSnackbar('Could not continue the conversation. Please try again.', {
          variant: 'error',
        });
      }
    },
    [addToolOutputs, enqueueSnackbar, messageId, requests, submitting, toolkit]
  );

  const handleToggleExpanded = useCallback(() => {
    setExpanded(current => !current);
  }, []);

  return (
    <PendingInteractionCardChrome
      expanded={expanded}
      footer={
        <>
          <CardSecondaryAction disabled={submitting} onClick={() => void answerPause('dismissed')}>
            Not now
          </CardSecondaryAction>
          <CardPrimaryAction disabled={submitting} onClick={() => void answerPause('enabled')}>
            Enable
          </CardPrimaryAction>
        </>
      }
      maxHeight={maxHeight}
      title={
        queuePosition && queuePosition.total > 1
          ? `${getExternalEnableTitle(toolkit)} (${queuePosition.current} of ${queuePosition.total})`
          : getExternalEnableTitle(toolkit)
      }
      toggleLabel="tool access"
      onToggleExpanded={handleToggleExpanded}
    >
      <Typography className={classes.body}>{getExternalEnableDescription(toolkit)}</Typography>
      <Typography className={classes.slugs}>
        {getExternalToolActionLabel({ toolSlugs: unionSlugs, toolkit })}
      </Typography>
    </PendingInteractionCardChrome>
  );
});
